import * as React from 'react'
import * as ReactRouter from 'react-router-dom'

import * as common from '@rushplay/common'
import { lazy as loadable } from '@loadable/component'
import { useI18n } from '@rushplay/i18n'

const SearchBar = loadable(() => import('../search-bar'))
const SearchProvider = loadable(() => import('./search-provider'))
const SearchResults = loadable(() => import('./search-results'))

// eslint-disable-next-line no-unused-vars
function removeKey(key, { [key]: removedKey, ...rest }) {
  return rest
}

function updateQuery(type, value, prevQuery) {
  if (!value.length) {
    return removeKey(type, prevQuery)
  }

  if (['providers', 'sub_providers'].includes(type)) {
    const previous = prevQuery[type] || []

    const updated = previous.includes(value)
      ? previous.filter(el => el !== value)
      : [...previous, value]

    return updated.length
      ? { ...prevQuery, [type]: updated }
      : removeKey(type, prevQuery)
  }

  return {
    ...prevQuery,
    [type]: value,
  }
}

function getQueryValue(type, search) {
  const query = search ? JSON.parse(decodeURIComponent(search)) : {}
  return query[type] || ''
}

function encodeQuery(query) {
  return encodeURIComponent(JSON.stringify(query))
}

function decodeQuery(query) {
  if (!query.length) {
    return {}
  }
  return JSON.parse(decodeURI(query))
}

function getSelectedProviders(querystring) {
  if (!querystring.length) {
    return []
  }

  const { sub_providers = [], providers = [] } = decodeQuery(querystring)

  return [...sub_providers, ...providers]
}

export function SearchPage() {
  const i18n = useI18n()
  const location = ReactRouter.useLocation()
  const history = ReactRouter.useHistory()
  const [isSearchReset, setIsSearchReset] = React.useState(false)

  const searchParams = new URLSearchParams(location.search)
  const searchQuery = searchParams.get('q') || ''

  const selectedProviders = getSelectedProviders(searchQuery)

  function onSearchQueryClear() {
    setIsSearchReset(true)
    const updatedQuery = updateQuery('text', '', decodeQuery(searchQuery))
    return Object.keys(updatedQuery).length
      ? history.replace(`${location.pathname}?q=${encodeQuery(updatedQuery)}`)
      : history.replace(`${location.pathname}`)
  }

  function onSearchQueryChange(value) {
    setIsSearchReset(false)
    const previousQuery = searchQuery.length ? decodeQuery(searchQuery) : {}
    const updatedQuery = updateQuery('text', value, previousQuery)

    return Object.keys(updatedQuery).length
      ? history.replace(`${location.pathname}?q=${encodeQuery(updatedQuery)}`)
      : history.replace(`${location.pathname}`)
  }

  function onProviderSearchChange(value) {
    const previousQuery = searchQuery.length ? decodeQuery(searchQuery) : {}
    const updatedQuery = updateQuery(value.type, value.name, previousQuery)

    return Object.keys(updatedQuery).length
      ? history.replace(`${location.pathname}?q=${encodeQuery(updatedQuery)}`)
      : history.replace(`${location.pathname}`)
  }

  function onProviderSearchReset() {
    const updatedQuery = ['providers', 'sub_providers'].reduce(
      (prevQuery, type) => updateQuery(type, '', prevQuery),
      decodeQuery(searchQuery)
    )
    return Object.keys(updatedQuery).length
      ? history.replace(`${location.pathname}?q=${encodeQuery(updatedQuery)}`)
      : history.replace(`${location.pathname}`)
  }

  const onSearchReset = React.useCallback(() => {
    if (!isSearchReset) {
      setIsSearchReset(true)
    }
    return history.replace(`${location.pathname}`)
  }, [isSearchReset])

  return (
    <common.Box flexGrow="1" flexShrink="1" flexBasis="0" overflow="hidden">
      <common.Space px={[1, 2]} pb={1}>
        <common.Box backgroundColor="nav" borderRadius={1} padding={[0, 2]}>
          <SearchBar
            autoFocus
            searchReset={isSearchReset}
            value={getQueryValue('text', searchQuery)}
            onClear={onSearchQueryClear}
            onChange={onSearchQueryChange}
          />
        </common.Box>
      </common.Space>
      <common.Space px={[1, 2]} pb={1}>
        <SearchProvider
          selectedProviders={selectedProviders}
          isLiveCasino={location?.pathname?.includes('live-casino')}
          onChange={onProviderSearchChange}
          onProviderSearchReset={onProviderSearchReset}
        />
      </common.Space>
      {searchQuery ? (
        <SearchResults
          searchQuery={decodeQuery(searchQuery)}
          onReset={onSearchReset}
        />
      ) : (
        <common.Box display="flex" justifyContent="center" alignItems="center">
          <common.Space pb={[1, 2]} pt={[1, 2]}>
            <common.Text fontFamily="head" textAlign="center" as="h1">
              {i18n.translate('search-results.incitament')}
            </common.Text>
          </common.Space>
        </common.Box>
      )}
    </common.Box>
  )
}

// For @loadable/components
export default SearchPage
